/**
 * v2 PB-Liste — Batch-13 Paket C.
 *
 * Ersetzt für /pflege und /anfragen den eingefrorenen v1-Endpunkt
 * `GET /v1/admin/users/2` (siehe `v1Users.byRole` in `src/lib/v1.ts`), der
 * NICHT mehr geändert werden darf (Parallelbetrieb-Regel, CRM 1.0/2.0).
 *
 * Server: `GET /v2/admin/pb` (App\Http\Controllers\v2\Admin\PbListController).
 * Gleiche Query-Parameter wie der bisherige v1-Endpunkt (page, per_page,
 * filters[...], orderBy[field], orderBy[profile], order) PLUS `scope`.
 *
 * Antwortform ist bewusst kompatibel mit `V1User` (inkl. `profiles`,
 * `bk_name`/`bk_user_id`, `assigned_admin_name`, `current_absence`, NEU:
 * `contact_name`/`contact_email` inline statt Lazy-Load pro Zeile) — die
 * bestehenden Mapper `v1UserToClient`/`v1UserToAnfrage` bleiben unverändert
 * nutzbar.
 */

import { apiV2 } from './api';
import type { ListParams, V1User } from './v1';

export type PbListScope = 'kunden' | 'anfragen' | 'alle';

interface PbListResponse {
  data: V1User[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  errors: unknown;
}

/**
 * Flatten ListParams + scope to bracket-notation query params, same
 * convention as v1.ts' internal (unexported) `buildParams`.
 */
function buildPbListParams(scope: PbListScope, p: ListParams = {}): Record<string, unknown> {
  const out: Record<string, unknown> = { scope };
  if (p.page !== undefined) out.page = p.page;
  if (p.perPage !== undefined) out.per_page = p.perPage;

  const filters = p.filters ?? {};
  const keys = Object.keys(filters);
  if (keys.length === 0) {
    out['filters[_]'] = '1';
  } else {
    for (const k of keys) {
      out[`filters[${k}]`] = filters[k];
    }
  }

  if (p.orderBy) {
    out['orderBy[field]'] = p.orderBy.field;
    if (p.orderBy.profile) out['orderBy[profile]'] = 'true';
  }
  if (p.order !== undefined) {
    out.order = p.order;
  }
  return out;
}

export const pbList = {
  /**
   * GET /v2/admin/pb?scope=kunden|anfragen|alle
   *
   * Returns the same paginated shape v1Users.byRole('PB', …) used to
   * return (`{ data, current_page, last_page, total, … }`), so call sites
   * built against that shape need no further changes beyond swapping the
   * import and adding the `scope`.
   */
  list: async (
    scope: PbListScope,
    params?: ListParams,
  ): Promise<{ data: V1User[]; current_page: number; last_page: number; per_page: number; total: number }> => {
    const res = await apiV2.get<PbListResponse>('/admin/pb', {
      params: buildPbListParams(scope, params),
    });
    return { data: res.data.data, ...res.data.meta };
  },

  /**
   * GET /v2/admin/pb/phase-counts?scope=…
   *
   * Same shape as the legacy `v1Users.pbPhaseCounts()` (`{ counts, total }`)
   * — used for the phase chips above the /pflege and /anfragen tables.
   */
  phaseCounts: async (
    scope: PbListScope,
    filters?: ListParams['filters'],
  ): Promise<{ counts: Record<string, number>; total: number }> => {
    const res = await apiV2.get<{ type: string; counts: Record<string, number>; total: number }>(
      '/admin/pb/phase-counts',
      { params: buildPbListParams(scope, { filters }) },
    );
    return { counts: res.data.counts, total: res.data.total };
  },
};
